import DataSource from "./DataSource";
import {Form} from "@/utils/Form";
import {
    UpdateDomainSettingsMutation,
    UpdateDomainSettingsMutationVariables,
    useUpdateDomainSettingsMutation,
} from "@/components/domain_settings/settings/mutations.generated";
import deepEqual from "deep-equal";
import {pruneDefaults, RecursiveKeyOf} from "@/tools";

export type DomainSettingsImageUrls = {
    loginLogo?: string;
    background?: string;
    logo?: string;
};
export type DomainSettingsValues = UpdateDomainSettingsMutationVariables["input"] & {
    shortNumbersLength: number;
    imageUrls: DomainSettingsImageUrls;
};

const basicPermittedFields: RecursiveKeyOf<DomainSettingsValues>[] = [
    "id",
    "baseColor",
    "logo",
    "login.backgroundColor",
    "login.logoImage",
    "background.color",
    "background.image",
    "background.isCentered",
    "sounds.moh",
    "sounds.park",
    "outgoingCallBarring.admin",
    "smtp.enabled",
    "smtp.username",
    "smtp.userpass",
] as const;

export default class DomainSettingsForm extends Form<
    UpdateDomainSettingsMutation,
    UpdateDomainSettingsMutationVariables,
    DomainSettingsValues
> {
    readonly dataSource: DataSource;
    private readonly canEditSuperuser: boolean;

    constructor(dataSource: DataSource, canEditSuperuser: boolean) {
        super("updateDomainSettings");
        this.hooks(useUpdateDomainSettingsMutation, dataSource.initialData);
        this.dataSource = dataSource;
        this.canEditSuperuser = canEditSuperuser;
    }

    updateFormValues() {
        this.setValues(this.dataSource.initialData);
    }

    isRestrictedField(fieldName: RecursiveKeyOf<DomainSettingsValues>): boolean {
        if (this.canEditSuperuser) {
            return false;
        }

        return !basicPermittedFields.includes(fieldName);
    }

    protected override get filteredVariables(): UpdateDomainSettingsMutationVariables {
        const copy = structuredClone(this.values);
        console.debug("Form values before cleaning:", copy);
        const cleaned = pruneDefaults(copy, this.initialData, {
            keepKeys: ["id"],
        });
        console.debug("Form values after cleaning:", cleaned);

        return {
            input: cleaned as UpdateDomainSettingsMutationVariables["input"],
        };
    }

    override get hasChanged(): boolean {
        return !deepEqual(this.initialData, this.values);
    }

    override submit(): Promise<boolean> {
        // reload domain theme after mutation
        return super.submit({
            evictCaches: [
                {
                    fieldName: "domainTheme",
                    args: {
                        domainId: this.values.id,
                    },
                },
            ],
        });
    }
}
